import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD306Query } from './hooks/useD306Query';
import type { StatRow, StatRowByProcess, UnitSummary, VendorSummary } from '../../services/d306Api';
import './D306Form.css';

const D306Form: React.FC = () => {
  const { loading, response, unitSummary, vendorSummary, queried, noData, runQuery, reset } = useD306Query();

  const [reportType, setReportType] = useState<1 | 2>(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [processFrom, setProcessFrom] = useState('');
  const [processTo, setProcessTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      vendor_from: vendorFrom.trim() || undefined,
      vendor_to: vendorTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
      process_from: processFrom.trim() || undefined,
      process_to: processTo.trim() || undefined,
      dept_from: deptFrom.trim() || undefined,
      dept_to: deptTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType(1);
    setDateFrom('');
    setDateTo('');
    setVendorFrom('');
    setVendorTo('');
    setProductFrom('');
    setProductTo('');
    setProcessFrom('');
    setProcessTo('');
    setDeptFrom('');
    setDeptTo('');
    reset();
  };

  const handlePrint = () => {
    if (!response || !response.data || response.data.length === 0) return;
    const title = reportType === 1 ? '託工單統計表（廠商別）' : '託工單統計表（廠商工繳別）';
    const win = window.open('', '_blank');
    if (!win) return;

    let tableHTML = '';
    if (reportType === 1) {
      const rows = response.data as StatRow[];
      tableHTML = `<table><thead><tr><th>廠商代號</th><th>廠商名稱</th><th>單位</th><th>數量</th><th>金額</th><th>平均單價</th></tr></thead><tbody>
${rows.map(r => `<tr><td>${r.FD130_12}</td><td>${r.vendor_name}</td><td>${r.FD130_19}</td><td class="right">${r.total_qty?.toFixed(2)}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.avg_price?.toFixed(3)}</td></tr>`).join('')}
</tbody></table>`;
    } else {
      const rows = response.data as StatRowByProcess[];
      tableHTML = `<table><thead><tr><th>廠商代號</th><th>廠商名稱</th><th>工繳代號</th><th>單位</th><th>數量</th><th>金額</th><th>平均單價</th></tr></thead><tbody>
${rows.map(r => `<tr><td>${r.FD130_12}</td><td>${r.vendor_name}</td><td>${r.FD130_15}</td><td>${r.FD130_19}</td><td class="right">${r.total_qty?.toFixed(2)}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.avg_price?.toFixed(3)}</td></tr>`).join('')}
</tbody></table>`;
    }

    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>${tableHTML}
<p style="margin-top:8px">共 ${response.data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  // Report 1 columns
  const columnsType1: ColumnsType<StatRow> = [
    { title: '廠商代號', dataIndex: 'FD130_12', key: 'FD130_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 150 },
    { title: '單位', dataIndex: 'FD130_19', key: 'FD130_19', width: 60 },
    { title: '數量', dataIndex: 'total_qty', key: 'total_qty', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '金額', dataIndex: 'total_amt', key: 'total_amt', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  // Report 2 columns
  const columnsType2: ColumnsType<StatRowByProcess> = [
    { title: '廠商代號', dataIndex: 'FD130_12', key: 'FD130_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 150 },
    { title: '工繳代號', dataIndex: 'FD130_15', key: 'FD130_15', width: 100 },
    { title: '單位', dataIndex: 'FD130_19', key: 'FD130_19', width: 60 },
    { title: '數量', dataIndex: 'total_qty', key: 'total_qty', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '金額', dataIndex: 'total_amt', key: 'total_amt', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  const summaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'FD130_19', key: 'FD130_19', width: 80 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  const vendorSummaryColumns: ColumnsType<VendorSummary> = [
    { title: '廠商代號', dataIndex: 'FD130_12', key: 'FD130_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', width: 150 },
    { title: '單位', dataIndex: 'FD130_19', key: 'FD130_19', width: 60 },
    { title: '小計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '小計金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D306} action="query">
      <div className="d306-container">
        <div className="d306-header">
          <h2>託工單統計表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio value={1}>廠商別</Radio>
              <Radio value={2}>廠商工繳別</Radio>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('託工日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('託工日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號起')}
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號迄')}
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號起')}
              <Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號迄')}
              <Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('工繳代號起')}
              <Input value={processFrom} onChange={e => setProcessFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('工繳代號迄')}
              <Input value={processTo} onChange={e => setProcessTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號起')}
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號迄')}
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && response && (
          <>
            {response.report_type === 1 ? (
              <Table<StatRow>
                columns={columnsType1}
                dataSource={(response.data as StatRow[])}
                rowKey={(r, i) => `${r.FD130_12}-${r.FD130_19}-${i}`}
                loading={loading}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
                scroll={{ x: 700 }}
                style={{ marginBottom: 16 }}
              />
            ) : (
              <>
                <Table<StatRowByProcess>
                  columns={columnsType2}
                  dataSource={(response.data as StatRowByProcess[])}
                  rowKey={(r, i) => `${r.FD130_12}-${r.FD130_15}-${r.FD130_19}-${i}`}
                  loading={loading}
                  size="small"
                  pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
                  scroll={{ x: 800 }}
                  style={{ marginBottom: 16 }}
                />
                {vendorSummary.length > 0 && (
                  <>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>廠商小計</div>
                    <Table<VendorSummary>
                      columns={vendorSummaryColumns}
                      dataSource={vendorSummary}
                      rowKey={(r, i) => `vs-${r.FD130_12}-${r.FD130_19}-${i}`}
                      size="small"
                      pagination={false}
                      scroll={{ x: 600 }}
                      style={{ marginBottom: 16 }}
                    />
                  </>
                )}
              </>
            )}
            {unitSummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>單位合計</div>
                <Table<UnitSummary>
                  columns={summaryColumns}
                  dataSource={unitSummary}
                  rowKey="FD130_19"
                  size="small"
                  pagination={false}
                  scroll={{ x: 500 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default D306Form;
